'use client';

import React, { useEffect, useState } from 'react';
import Image from 'next/image';
import {
  Calendar,
  Clock,
  MapPin,
  CheckCircle2,
  XCircle,
  FileText,
  Heart,
  ShieldCheck,
  Edit2,
  Trash2,
  AlertCircle
} from 'lucide-react';
import { Booking, Vehicle } from '@/types';
import { CustomerAuthPanel } from '@/components/site/CustomerAuthPanel';
import { useCurrency } from '@/components/site/CurrencyContext';
import { useLocale } from '@/lib/i18n';
import { rememberCustomer } from '@/lib/session';
import { CarIllustration } from './CarIllustrations';

interface UserProfileViewProps {
  bookings: Booking[];
  vehicles: Vehicle[];
  favorites: string[];
  onCancelBooking: (id: string) => void;
  onModifyBookingDates: (id: string, newPickupDate: string, newDropoffDate: string) => void;
  onBookVehicle: (vehicle: Vehicle) => void;
  onToggleFavorite: (id: string) => void;
  onViewVehicleDetails?: (vehicle: Vehicle) => void;
}

interface AccountProfile {
  id: string;
  name: string;
  firstName?: string | null;
  lastName?: string | null;
  email: string;
  emailVerified?: boolean;
  phone: string;
  licenseNumber: string;
  locale?: string;
  avatarUrl?: string | null;
}

interface PhoneChallenge {
  challengeId: string;
  phoneMask?: string;
  demoCode?: string;
}

interface ProfileForm {
  firstName: string;
  lastName: string;
  name: string;
  phone: string;
  licenseNumber: string;
  locale: string;
}

export function UserProfileView({
  bookings,
  vehicles,
  favorites,
  onCancelBooking,
  onModifyBookingDates,
  onBookVehicle,
  onToggleFavorite,
  onViewVehicleDetails,
}: UserProfileViewProps) {
  const { fmt, equiv } = useCurrency();
  const { t } = useLocale();
  const [activeTab, setActiveTab] = useState<'bookings' | 'history' | 'favorites' | 'settings'>('bookings');
  const [cancellingBookingId, setCancellingBookingId] = useState<string | null>(null);
  const [modifyingBooking, setModifyingBooking] = useState<Booking | null>(null);
  const [modPickupDate, setModPickupDate] = useState('');
  const [modDropoffDate, setModDropoffDate] = useState('');
  const [account, setAccount] = useState<AccountProfile | null>(null);
  const [accountReady, setAccountReady] = useState(false);
  const [serverBookings, setServerBookings] = useState<Booking[] | null>(null);
  const [authMode, setAuthMode] = useState<'login' | 'register'>('login');
  const [authError, setAuthError] = useState<string | null>(null);
  const [authPending, setAuthPending] = useState(false);
  const [authForm, setAuthForm] = useState({ firstName: '', lastName: '', email: '', password: '', phone: '', licenseNumber: '' });
  const [challenge, setChallenge] = useState<PhoneChallenge | null>(null);
  const [code, setCode] = useState('');
  const [profileForm, setProfileForm] = useState<ProfileForm>({ firstName: '', lastName: '', name: '', phone: '', licenseNumber: '', locale: 'fr' });
  const [profileMessage, setProfileMessage] = useState<string | null>(null);
  const [currentPassword, setCurrentPassword] = useState('');
  const [nextPassword, setNextPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [remember, setRemember] = useState(true);
  const [recovery, setRecovery] = useState<'login' | 'forgot' | 'reset'>('login');
  const [emailCode, setEmailCode] = useState('');
  const [emailDemo, setEmailDemo] = useState<string | null>(null);
  const [emailSent, setEmailSent] = useState(false);
  const [resetCode, setResetCode] = useState('');
  const [resetPassword, setResetPassword] = useState('');
  const [resetConfirm, setResetConfirm] = useState('');

  const visibleBookings = serverBookings ?? bookings;

  const loadAccount = async () => {
    const me = await fetch('/api/account');
    if (!me.ok) {
      setAccount(null);
      setServerBookings(null);
      setAccountReady(true);
      return;
    }
    const profile = (await me.json()) as AccountProfile;
    const list = await fetch('/api/account/bookings');
    const rows = list.ok ? await list.json() : [];
    setAccount(profile);
    setProfileForm(profileFields(profile));
    setServerBookings(Array.isArray(rows) ? rows : []);
    setAccountReady(true);
  };

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const me = await fetch('/api/account');
        if (cancelled) return;
        if (!me.ok) {
          setAccountReady(true);
          return;
        }
        const profile = (await me.json()) as AccountProfile;
        const list = await fetch('/api/account/bookings');
        const rows = list.ok ? await list.json() : [];
        if (cancelled) return;
        setAccount(profile);
        setProfileForm(profileFields(profile));
        setServerBookings(Array.isArray(rows) ? rows : []);
      } finally {
        if (!cancelled) setAccountReady(true);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  // Filter bookings
  const activeBookings = visibleBookings.filter((b) => b.status === 'confirmed' || b.status === 'active');
  const pastBookings = visibleBookings.filter((b) => b.status === 'completed' || b.status === 'cancelled');

  // Favorite vehicles
  const favoriteVehicles = vehicles.filter((v) => favorites.includes(v.id));

  const handleOpenModifyModal = (booking: Booking) => {
    setModifyingBooking(booking);
    setModPickupDate(booking.pickupDate);
    setModDropoffDate(booking.dropoffDate);
  };

  const handleSaveModification = async () => {
    if (!modifyingBooking || !modPickupDate || !modDropoffDate) return;
    if (account) {
      const res = await fetch(`/api/account/bookings/${modifyingBooking.id}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ pickupDate: modPickupDate, dropoffDate: modDropoffDate }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        setAuthError(typeof data.error === 'string' ? data.error : t('prof.modD'));
        return;
      }
      await loadAccount();
    } else {
      onModifyBookingDates(modifyingBooking.id, modPickupDate, modDropoffDate);
    }
    setModifyingBooking(null);
  };

  const confirmCancel = async () => {
    if (!cancellingBookingId) return;
    if (account) {
      const res = await fetch(`/api/account/bookings/${cancellingBookingId}/cancel`, { method: 'POST' });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        setAuthError(typeof data.error === 'string' ? data.error : t('prof.cancelT'));
        return;
      }
      await loadAccount();
    } else {
      onCancelBooking(cancellingBookingId);
    }
    setCancellingBookingId(null);
  };

  const submitAuth = async (event: React.FormEvent) => {
    event.preventDefault();
    setAuthPending(true);
    setAuthError(null);
    try {
      const path = authMode === 'register' ? '/api/account/register' : '/api/account/login';
      const body = authMode === 'register'
        ? {
            firstName: authForm.firstName,
            lastName: authForm.lastName,
            name: `${authForm.firstName} ${authForm.lastName}`.trim(),
            email: authForm.email,
            password: authForm.password,
            phone: authForm.phone,
            licenseNumber: authForm.licenseNumber,
          }
        : { email: authForm.email, password: authForm.password, remember };
      if (authMode === 'login') rememberCustomer(remember);
      const res = await fetch(path, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      const data = await res.json().catch(() => ({}));
      if (res.status === 202 && typeof data.challengeId === 'string') {
        setChallenge({
          challengeId: data.challengeId,
          phoneMask: typeof data.phoneMask === 'string' ? data.phoneMask : undefined,
          demoCode: typeof data.demoCode === 'string' ? data.demoCode : undefined,
        });
        setCode('');
        return;
      }
      if (!res.ok) {
        setAuthError(messageFrom(data, t('prof.signIn')));
        return;
      }
      await loadAccount();
    } catch {
      setAuthError(t('prof.signIn'));
    } finally {
      setAuthPending(false);
    }
  };

  const signOut = async () => {
    await fetch('/api/account/logout', { method: 'POST' });
    setAccount(null);
    setServerBookings(null);
  };

  const verifyCode = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!challenge || authPending) return;
    setAuthPending(true);
    setAuthError(null);
    try {
      const res = await fetch('/api/account/phone/verify', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ challengeId: challenge.challengeId, code, remember }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        setAuthError(messageFrom(data, t('prof.code')));
        return;
      }
      setChallenge(null);
      setCode('');
      if (data.customer && typeof data.customer === 'object') {
        const profile = data.customer as AccountProfile;
        setAccount(profile);
        setProfileForm(profileFields(profile));
      }
      await loadAccount();
    } finally {
      setAuthPending(false);
    }
  };

  const resendCode = async () => {
    if (!challenge || authPending) return;
    setAuthPending(true);
    setAuthError(null);
    try {
      const res = await fetch('/api/account/phone/resend', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ challengeId: challenge.challengeId }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        setAuthError(messageFrom(data, t('prof.resend')));
        return;
      }
      setChallenge({
        challengeId: typeof data.challengeId === 'string' ? data.challengeId : challenge.challengeId,
        phoneMask: typeof data.phoneMask === 'string' ? data.phoneMask : challenge.phoneMask,
        demoCode: typeof data.demoCode === 'string' ? data.demoCode : undefined,
      });
    } finally {
      setAuthPending(false);
    }
  };

  const saveProfile = async () => {
    if (!account) return;
    const res = await fetch('/api/account', {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        ...profileForm,
        name: `${profileForm.firstName} ${profileForm.lastName}`.trim() || profileForm.name,
      }),
    });
    const data = await res.json().catch(() => ({}));
    if (res.status === 202 && typeof data.challengeId === 'string') {
      setChallenge({
        challengeId: data.challengeId,
        phoneMask: typeof data.phoneMask === 'string' ? data.phoneMask : undefined,
        demoCode: typeof data.demoCode === 'string' ? data.demoCode : undefined,
      });
      setCode('');
      setActiveTab('settings');
      setProfileMessage(t('prof.codeHint'));
      return;
    }
    if (!res.ok) {
      setProfileMessage(messageFrom(data, t('prof.save')));
      return;
    }
    setAccount(data as AccountProfile);
    setProfileForm(profileFields(data as AccountProfile));
    setProfileMessage(t('prof.saved'));
  };

  const verifyEmail = async () => {
    const res = await fetch('/api/account/email/otp', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: '{}' });
    const data = await res.json().catch(() => ({}));
    if (!res.ok) {
      setProfileMessage(messageFrom(data, t('prof.verifyEmail')));
      return;
    }
    setEmailDemo(typeof data.demoCode === 'string' ? data.demoCode : null);
    setEmailSent(true);
    setEmailCode('');
    setProfileMessage(t('prof.emailReady'));
  };

  const confirmEmailCode = async (event: React.FormEvent) => {
    event.preventDefault();
    const res = await fetch('/api/account/email/otp/confirm', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ code: emailCode }),
    });
    const data = await res.json().catch(() => ({}));
    if (!res.ok) {
      setProfileMessage(messageFrom(data, t('prof.emailCode')));
      return;
    }
    setAccount(data as AccountProfile);
    setEmailCode('');
    setEmailDemo(null);
    setEmailSent(false);
    setProfileMessage(t('prof.emailVerified'));
  };

  const requestReset = async (event: React.FormEvent) => {
    event.preventDefault();
    setAuthPending(true);
    setAuthError(null);
    try {
      const res = await fetch('/api/account/password/forgot', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: authForm.email }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        setAuthError(messageFrom(data, t('prof.forgot')));
        return;
      }
      setEmailDemo(typeof data.demoCode === 'string' ? data.demoCode : null);
      setRecovery('reset');
    } catch {
      setAuthError(t('prof.forgot'));
    } finally {
      setAuthPending(false);
    }
  };

  const submitReset = async (event: React.FormEvent) => {
    event.preventDefault();
    if (resetPassword !== resetConfirm) {
      setAuthError(t('prof.passwordMismatch'));
      return;
    }
    setAuthPending(true);
    setAuthError(null);
    try {
      const res = await fetch('/api/account/password/reset', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email: authForm.email,
          code: resetCode,
          password: resetPassword,
          password_confirmation: resetConfirm,
        }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        setAuthError(messageFrom(data, t('prof.resetPassword')));
        return;
      }
      setRecovery('login');
      setResetCode('');
      setResetPassword('');
      setResetConfirm('');
      setEmailDemo(null);
      setAuthError(null);
      setProfileMessage(t('prof.passwordReset'));
    } catch {
      setAuthError(t('prof.resetPassword'));
    } finally {
      setAuthPending(false);
    }
  };

  const changePassword = async () => {
    if (nextPassword !== confirmPassword) {
      setProfileMessage(t('prof.passwordMismatch'));
      return;
    }
    const res = await fetch('/api/account/password', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ currentPassword, password: nextPassword, password_confirmation: confirmPassword }),
    });
    const data = await res.json().catch(() => ({}));
    if (!res.ok) {
      setProfileMessage(messageFrom(data, t('prof.changePassword')));
      return;
    }
    setCurrentPassword('');
    setNextPassword('');
    setConfirmPassword('');
    setProfileMessage(t('prof.passwordSaved'));
  };

  const uploadAvatar = async (file: File | null) => {
    if (!account || authPending) return;
    setAuthPending(true);
    setProfileMessage(null);
    try {
      const res = file === null
        ? await fetch('/api/account/avatar', { method: 'DELETE' })
        : await fetch('/api/account/avatar', { method: 'POST', body: (() => { const body = new FormData(); body.set('avatar', file); return body; })() });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        setProfileMessage(messageFrom(data, t('prof.avatar')));
        return;
      }
      setAccount(data as AccountProfile);
    } finally {
      setAuthPending(false);
    }
  };

  const initials = (account?.name ?? '')
    .split(/\s+/)
    .slice(0, 2)
    .map((part) => part[0]?.toUpperCase() ?? '')
    .join('') || 'DX';

  if (!account) {
    return (
      <CustomerAuthPanel
        ready={accountReady}
        authMode={authMode}
        recovery={recovery}
        authForm={authForm}
        challenge={challenge}
        code={code}
        remember={remember}
        authError={authError}
        authPending={authPending}
        notice={profileMessage}
        emailDemo={emailDemo}
        resetCode={resetCode}
        resetPassword={resetPassword}
        resetConfirm={resetConfirm}
        onAuthMode={(mode) => { setAuthMode(mode); setAuthError(null); setRecovery('login'); setProfileMessage(null); }}
        onRecovery={(mode) => { setRecovery(mode); setAuthError(null); }}
        onAuthForm={setAuthForm}
        onCode={setCode}
        onRemember={setRemember}
        onResetCode={setResetCode}
        onResetPassword={setResetPassword}
        onResetConfirm={setResetConfirm}
        onSubmitAuth={(event) => void submitAuth(event)}
        onRequestReset={(event) => void requestReset(event)}
        onSubmitReset={(event) => void submitReset(event)}
        onVerifyCode={(event) => void verifyCode(event)}
        onResendCode={() => void resendCode()}
      />
    );
  }

  return (
    <div className="w-full bg-slate-50 min-h-screen py-10">
      <div className="max-w-7xl mx-auto px-4 sm:px-8">
        <div className="bg-[#11161f] text-white rounded-3xl p-6 sm:p-8 shadow-xl border border-slate-800 mb-8">
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-6">
            <div className="flex items-center gap-5">
              {account.avatarUrl ? (
                <img src={account.avatarUrl} alt="" className="w-16 h-16 sm:w-20 sm:h-20 rounded-2xl object-cover shrink-0" />
              ) : (
                <div className="w-16 h-16 sm:w-20 sm:h-20 rounded-2xl bg-gradient-to-tr from-[var(--color-primary)] to-amber-300 flex items-center justify-center text-slate-950 font-black text-2xl shadow-lg shrink-0">
                  {initials}
                </div>
              )}
              <div>
                <h1 className="text-xl sm:text-2xl font-extrabold text-white tracking-tight">{account.name}</h1>
                <p className="text-xs text-slate-400 mt-1">
                  {account.email}
                  {account.licenseNumber ? (
                    <> · {t('prof.license')}: <strong className="text-slate-300 font-mono">{account.licenseNumber}</strong></>
                  ) : null}
                </p>
                <p className="text-xs text-slate-300 mt-3">{visibleBookings.length} {t('prof.tabActive')}</p>
              </div>
            </div>
            <button type="button" onClick={() => void signOut()} className="text-xs font-bold text-slate-300 hover:text-white">
              {t('prof.signOut')}
            </button>
          </div>
        </div>

        {/* Dashboard Tabs */}
        <div className="flex items-center gap-2 border-b border-slate-200 mb-8 overflow-x-auto no-scrollbar">
          <button
            type="button"
            onClick={() => setActiveTab('bookings')}
            className={`pb-3 px-3 text-xs font-bold tracking-wider uppercase transition-all cursor-pointer whitespace-nowrap relative ${
              activeTab === 'bookings'
                ? 'text-[var(--color-primary)]'
                : 'text-slate-600 hover:text-slate-900'
            }`}
          >
            <span>{t('prof.tabActive')} ({activeBookings.length})</span>
            {activeTab === 'bookings' && (
              <span className="absolute bottom-0 left-0 w-full h-[2px] bg-[var(--color-primary)]" />
            )}
          </button>

          <button
            type="button"
            onClick={() => setActiveTab('history')}
            className={`pb-3 px-3 text-xs font-bold tracking-wider uppercase transition-all cursor-pointer whitespace-nowrap relative ${
              activeTab === 'history'
                ? 'text-[var(--color-primary)]'
                : 'text-slate-600 hover:text-slate-900'
            }`}
          >
            <span>{t('prof.tabPast')} ({pastBookings.length})</span>
            {activeTab === 'history' && (
              <span className="absolute bottom-0 left-0 w-full h-[2px] bg-[var(--color-primary)]" />
            )}
          </button>

          <button
            type="button"
            onClick={() => setActiveTab('favorites')}
            className={`pb-3 px-3 text-xs font-bold tracking-wider uppercase transition-all cursor-pointer whitespace-nowrap relative ${
              activeTab === 'favorites'
                ? 'text-[var(--color-primary)]'
                : 'text-slate-600 hover:text-slate-900'
            }`}
          >
            <span>{t('prof.tabFav')} ({favoriteVehicles.length})</span>
            {activeTab === 'favorites' && (
              <span className="absolute bottom-0 left-0 w-full h-[2px] bg-[var(--color-primary)]" />
            )}
          </button>

          <button
            type="button"
            onClick={() => setActiveTab('settings')}
            className={`pb-3 px-3 text-xs font-bold tracking-wider uppercase transition-all cursor-pointer whitespace-nowrap relative ${
              activeTab === 'settings'
                ? 'text-[var(--color-primary)]'
                : 'text-slate-600 hover:text-slate-900'
            }`}
          >
            <span>{t('prof.tabSettings')}</span>
            {activeTab === 'settings' && (
              <span className="absolute bottom-0 left-0 w-full h-[2px] bg-[var(--color-primary)]" />
            )}
          </button>
        </div>

        {/* TAB 1: ACTIVE BOOKINGS */}
        {activeTab === 'bookings' && (
          <div>
            {activeBookings.length === 0 ? (
              <div className="bg-white rounded-2xl border border-slate-200 p-12 text-center">
                <Calendar className="w-10 h-10 text-slate-300 mx-auto mb-3" />
                <h3 className="text-base font-bold text-slate-900">{t('prof.emptyActive')}</h3>
                <p className="text-xs text-slate-500 mt-1 max-w-sm mx-auto mb-6">{t('prof.emptyActiveD')}</p>
                <button
                  type="button"
                  onClick={() => setActiveTab('favorites')}
                  className="px-5 py-2.5 bg-[var(--color-primary)] text-white font-bold text-xs rounded-xl"
                >{t('prof.browse')}</button>
              </div>
            ) : (
              <div className="space-y-4">
                {activeBookings.map((booking) => {
                  const vehicleObj = vehicles.find((v) => v.id === booking.vehicleId);

                  return (
                    <div
                      key={booking.id}
                      className="bg-white rounded-2xl border border-slate-200 p-5 sm:p-6 shadow-xs hover:shadow-md transition-all flex flex-col lg:flex-row items-start lg:items-center justify-between gap-6"
                    >
                      {/* Left: Vehicle Image & Info */}
                      <div className="flex flex-col sm:flex-row items-start sm:items-center gap-5 grow">
                        <div className="w-32 h-20 bg-slate-50 border border-slate-100 rounded-xl p-2 flex items-center justify-center shrink-0">
                          <CarIllustration
                            type={vehicleObj?.imageType || 'bmw_5series'}
                            className="w-full h-auto"
                          />
                        </div>

                        <div className="space-y-1">
                          <div className="flex items-center gap-2">
                            <span className="px-2 py-0.5 rounded-md bg-amber-50 text-[var(--color-primary)] font-mono text-[11px] font-bold">
                              #{booking.id}
                            </span>
                            <span className="px-2 py-0.5 rounded-full bg-emerald-50 text-emerald-700 text-[10px] font-bold uppercase tracking-wider">
                              {booking.status}
                            </span>
                          </div>

                          <h3 className="text-lg font-extrabold text-slate-900">
                            {booking.vehicleName}
                          </h3>

                          <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-1 text-xs text-slate-500 mt-2">
                            <div className="flex items-center gap-1.5">
                              <MapPin className="w-3.5 h-3.5 text-[var(--color-primary)] shrink-0" />
                              <span className="truncate max-w-[220px]">{booking.pickupLocation}</span>
                            </div>
                            <div className="flex items-center gap-1.5">
                              <Calendar className="w-3.5 h-3.5 text-[var(--color-primary)] shrink-0" />
                              <span>{booking.pickupDate} ({booking.pickupTime})</span>
                            </div>
                            <div className="flex items-center gap-1.5">
                              <Clock className="w-3.5 h-3.5 text-[var(--color-primary)] shrink-0" />
                              <span>{t('prof.retLabel')} {booking.dropoffDate} ({booking.dropoffTime})</span>
                            </div>
                            <div className="flex items-center gap-1.5">
                              <ShieldCheck className="w-3.5 h-3.5 text-emerald-500 shrink-0" />
                              <span>{booking.protectionPlan.toUpperCase()} Protection</span>
                            </div>
                          </div>
                        </div>
                      </div>

                      {/* Right: Price & Action Buttons */}
                      <div className="flex flex-col sm:flex-row lg:flex-col items-start lg:items-end justify-between w-full lg:w-auto gap-4 pt-4 lg:pt-0 border-t lg:border-t-0 border-slate-100">
                        <div className="text-left lg:text-right">
                          <span className="text-xs text-slate-400 block font-medium">{t('prof.totalPaid')}</span>
                          <span className="text-2xl font-black text-[var(--color-primary)] font-mono">
                            {fmt(booking.totalPrice)}
                            {equiv(booking.totalPrice) ? (
                              <span className="block text-[11px] font-medium text-slate-400">{equiv(booking.totalPrice)}</span>
                            ) : null}
                          </span>
                        </div>

                        <div className="flex items-center gap-2">
                          {booking.status === 'confirmed' && (
                            <button
                              type="button"
                              onClick={() => handleOpenModifyModal(booking)}
                              className="flex items-center gap-1 px-3 py-2 bg-slate-100 hover:bg-slate-200 text-slate-700 text-xs font-bold rounded-lg transition-colors cursor-pointer"
                            >
                              <Edit2 className="w-3.5 h-3.5" />
                              <span>{t('prof.modify')}</span>
                            </button>
                          )}

                          <button
                            type="button"
                            onClick={() => setCancellingBookingId(booking.id)}
                            className="flex items-center gap-1 px-3 py-2 border border-rose-200 text-rose-600 hover:bg-rose-50 text-xs font-bold rounded-lg transition-colors cursor-pointer"
                          >
                            <Trash2 className="w-3.5 h-3.5" />
                            <span>{t('prof.cancel')}</span>
                          </button>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}

        {/* TAB 2: PAST TRIPS */}
        {activeTab === 'history' && (
          <div className="space-y-4">
            {pastBookings.length === 0 ? (
              <div className="bg-white rounded-2xl border border-slate-200 p-12 text-center text-slate-500 text-xs">{t('prof.emptyPast')}</div>
            ) : (
              pastBookings.map((b) => (
                <div
                  key={b.id}
                  className="bg-white rounded-2xl border border-slate-200 p-5 flex items-center justify-between gap-4"
                >
                  <div>
                    <div className="flex items-center gap-2">
                      <span className="font-mono text-xs font-bold text-slate-700">#{b.id}</span>
                      <span className={`px-2 py-0.5 rounded text-[10px] font-bold uppercase ${
                        b.status === 'completed'
                          ? 'bg-slate-100 text-slate-700'
                          : 'bg-rose-50 text-rose-600'
                      }`}>
                        {b.status}
                      </span>
                    </div>
                    <h4 className="text-sm font-extrabold text-slate-900 mt-1">{b.vehicleName}</h4>
                    <p className="text-xs text-slate-400 mt-0.5">
                      {b.pickupDate} → {b.dropoffDate} · {b.pickupLocation}
                    </p>
                  </div>

                  <div className="text-right">
                    <span className="text-base font-black text-slate-800 font-mono">{fmt(b.totalPrice)}</span>
                    <button
                      type="button"
                      onClick={() => alert(`${t('prof.receipt')} ${b.id}!`)}
                      className="block text-xs font-bold text-[var(--color-primary)] hover:underline mt-1"
                    >{t('prof.invoice')}</button>
                  </div>
                </div>
              ))
            )}
          </div>
        )}

        {/* TAB 3: SAVED FAVORITES */}
        {activeTab === 'favorites' && (
          <div>
            {favoriteVehicles.length === 0 ? (
              <div className="bg-white rounded-2xl border border-slate-200 p-12 text-center">
                <Heart className="w-10 h-10 text-slate-300 mx-auto mb-2" />
                <h3 className="text-sm font-bold text-slate-900">{t('prof.emptyFav')}</h3>
                <p className="text-xs text-slate-500 mt-1">{t('prof.emptyFavD')}</p>
              </div>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                {favoriteVehicles.map((car) => (
                  <div
                    key={car.id}
                    className="bg-white rounded-2xl border border-slate-200 p-4 flex flex-col justify-between"
                  >
                    <div>
                      <div className="flex justify-between items-center mb-2">
                        <span className="text-[10px] font-bold text-slate-400 uppercase">{car.category}</span>
                        <button
                          type="button"
                          onClick={() => onToggleFavorite(car.id)}
                          className="p-1 text-rose-500 hover:text-slate-400"
                        >
                          <Heart className="w-4 h-4 fill-rose-500" />
                        </button>
                      </div>
                      <div className="relative w-full h-36 bg-slate-50 rounded-xl overflow-hidden mb-3">
                        {car.imageUrl ? (
                          <Image
                            src={car.imageUrl}
                            alt={car.name}
                            fill
                            className="object-cover"
                            referrerPolicy="no-referrer"
                          />
                        ) : (
                          <CarIllustration type={car.imageType} className="w-full h-auto max-h-28 p-2" />
                        )}
                      </div>
                      <h4 className="text-base font-bold text-slate-900">{car.name}</h4>
                      <p className="text-xs text-slate-500 mt-1">
                        {car.seats} {t('cat.seatsU')} · {car.transmission} · {fmt(car.pricePerDay)}/day
                      </p>
                    </div>
                    <div className="mt-4 flex gap-2">
                      {onViewVehicleDetails && (
                        <button
                          type="button"
                          onClick={() => onViewVehicleDetails(car)}
                          className="grow py-2.5 bg-slate-100 hover:bg-slate-200 text-slate-700 text-xs font-bold rounded-xl transition-all"
                        >{t('prof.viewDetails')}</button>
                      )}
                      <button
                        type="button"
                        onClick={() => onBookVehicle(car)}
                        className="grow py-2.5 bg-[var(--color-primary)] hover:bg-[var(--color-primary-dark)] text-white text-xs font-bold rounded-xl transition-all"
                      >{t('prof.rentNow')}</button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* TAB 4: SETTINGS */}
        {activeTab === 'settings' && (
          <div className="max-w-2xl space-y-4">
            <h3 className="text-base font-extrabold text-slate-900">{t('prof.settingsT')}</h3>
            {profileMessage ? <p className="text-xs font-semibold text-slate-700" role="status">{profileMessage}</p> : null}

            <section className="bg-white rounded-2xl border border-slate-200 p-5 space-y-3">
              <h4 className="text-sm font-extrabold text-slate-900">{t('prof.name')}</h4>
              <div className="flex items-center gap-3">
                {account?.avatarUrl ? (
                  <img src={account.avatarUrl} alt="" className="h-14 w-14 rounded-2xl object-cover" />
                ) : (
                  <span className="flex h-14 w-14 items-center justify-center rounded-2xl bg-slate-900 text-xs font-bold text-white">{initials}</span>
                )}
                <label className="text-xs font-bold text-slate-700">
                  {t('prof.avatar')}
                  <input type="file" accept="image/jpeg,image/png,image/webp" disabled={!account || authPending} onChange={(e) => void uploadAvatar(e.target.files?.[0] ?? null)} className="mt-1 block font-normal" />
                </label>
              </div>
              <div className="grid gap-3 sm:grid-cols-2">
                <label className="text-xs font-bold text-slate-700">{t('prof.firstName')} *
                  <input type="text" required value={account ? profileForm.firstName : ''} disabled={!account} onChange={(e) => setProfileForm({ ...profileForm, firstName: e.target.value })} className="mt-1 w-full p-2.5 bg-slate-50 border border-slate-200 rounded-xl font-normal" />
                </label>
                <label className="text-xs font-bold text-slate-700">{t('prof.lastName')} *
                  <input type="text" required value={account ? profileForm.lastName : ''} disabled={!account} onChange={(e) => setProfileForm({ ...profileForm, lastName: e.target.value })} className="mt-1 w-full p-2.5 bg-slate-50 border border-slate-200 rounded-xl font-normal" />
                </label>
                <label className="text-xs font-bold text-slate-700">{t('prof.dl')}
                  <input type="text" value={account ? profileForm.licenseNumber : ''} disabled={!account} onChange={(e) => setProfileForm({ ...profileForm, licenseNumber: e.target.value })} className="mt-1 w-full p-2.5 bg-slate-50 border border-slate-200 rounded-xl font-mono font-normal" />
                </label>
                <label className="text-xs font-bold text-slate-700">{t('prof.locale')}
                  <select value={profileForm.locale} disabled={!account} onChange={(e) => setProfileForm({ ...profileForm, locale: e.target.value })} className="mt-1 w-full p-2.5 bg-slate-50 border border-slate-200 rounded-xl font-normal">
                    <option value="fr">Français</option>
                    <option value="en">English</option>
                    <option value="ar">العربية</option>
                  </select>
                </label>
              </div>
              <button type="button" disabled={!account || authPending} onClick={() => void saveProfile()} className="px-5 py-2.5 bg-slate-900 text-white font-bold text-xs rounded-xl disabled:opacity-50">{t('prof.save')}</button>
            </section>

            <section className="bg-white rounded-2xl border border-slate-200 p-5">
              <div className="grid gap-6 sm:grid-cols-2">
                <div className="space-y-3">
                  <h4 className="text-sm font-extrabold text-slate-900">{t('prof.email')}</h4>
                  <p className="text-sm text-slate-800">{account?.email}</p>
                  <p className={`text-[11px] font-bold ${account?.emailVerified ? 'text-emerald-600' : 'text-amber-600'}`}>{account?.emailVerified ? t('prof.emailVerified') : t('prof.emailUnverified')}</p>
                  <p className="text-xs text-slate-500">{t('prof.emailHint')}</p>
                  <button type="button" disabled={!account || authPending} onClick={() => void verifyEmail()} className="rounded-xl border border-slate-300 px-3 py-1.5 text-[11px] font-bold text-slate-700 disabled:opacity-50">{t('prof.verifyEmail')}</button>
                  {emailSent && (
                    <form onSubmit={(event) => void confirmEmailCode(event)} className="flex flex-wrap items-center gap-2">
                      <input inputMode="numeric" maxLength={6} value={emailCode} onChange={(event) => setEmailCode(event.target.value.replace(/\D/g, '').slice(0, 6))} placeholder={t('prof.emailCode')} className="w-32 rounded-xl border border-slate-200 px-3 py-1.5 text-xs" />
                      <button type="submit" disabled={!account || authPending || emailCode.length !== 6} className="rounded-xl bg-slate-900 px-3 py-1.5 text-[11px] font-bold text-white disabled:opacity-50">{t('prof.confirmCode')}</button>
                    </form>
                  )}
                  {emailDemo ? <p className="text-[11px] font-semibold text-amber-700">{t('prof.demoCode')}: {emailDemo}</p> : null}
                </div>
                <div className="space-y-3 border-t border-slate-200 pt-6 sm:border-l sm:border-t-0 sm:pl-6 sm:pt-0">
                  <h4 className="text-sm font-extrabold text-slate-900">{t('prof.phone')}</h4>
                  <p className="text-xs text-slate-500">{t('prof.phoneHint')}</p>
                  <input type="tel" value={account ? profileForm.phone : ''} disabled={!account} onChange={(e) => setProfileForm({ ...profileForm, phone: e.target.value })} className="w-full p-2.5 bg-slate-50 border border-slate-200 rounded-xl text-sm" />
                  <button type="button" disabled={!account || authPending} onClick={() => void saveProfile()} className="rounded-xl border border-slate-300 px-3 py-1.5 text-[11px] font-bold text-slate-700 disabled:opacity-50">{t('prof.verify')}</button>
                  {authError ? <p className="text-xs font-semibold text-rose-600" role="alert">{authError}</p> : null}
                  {account && challenge && (
                    <form onSubmit={(event) => void verifyCode(event)} className="space-y-2">
                      <p className="text-xs text-slate-500">{t('prof.codeHint')} {challenge.phoneMask ?? ''}</p>
                      {challenge.demoCode ? <p className="text-[11px] font-semibold text-amber-700">{t('prof.demoCode')}: {challenge.demoCode}</p> : null}
                      <input required inputMode="numeric" autoComplete="one-time-code" placeholder={t('prof.code')} value={code} onChange={(e) => setCode(e.target.value)} className="w-full rounded-xl border border-slate-200 px-3 py-2 text-sm" />
                      <div className="flex gap-3">
                        <button type="submit" disabled={authPending} className="rounded-xl bg-slate-900 px-3 py-1.5 text-[11px] font-bold text-white disabled:opacity-50">{t('prof.verify')}</button>
                        <button type="button" disabled={authPending} onClick={() => void resendCode()} className="text-[11px] font-bold text-slate-600">{t('prof.resend')}</button>
                      </div>
                    </form>
                  )}
                </div>
              </div>
            </section>

            <section className="bg-white rounded-2xl border border-slate-200 p-5 space-y-3">
              <h4 className="text-sm font-extrabold text-slate-900">{t('prof.changePassword')}</h4>
              <p className="text-xs text-slate-500">{t('prof.passwordHint')}</p>
              <p className="text-xs text-slate-500">{t('prof.forgotHint')}</p>
              <label className="block text-xs font-bold text-slate-700">{t('prof.currentPassword')}
                <input type="password" value={currentPassword} disabled={!account} onChange={(e) => setCurrentPassword(e.target.value)} className="mt-1 w-full p-2.5 bg-slate-50 border border-slate-200 rounded-xl font-normal" />
              </label>
              <div className="grid gap-3 sm:grid-cols-2">
                <label className="text-xs font-bold text-slate-700">{t('prof.newPassword')}
                  <input type="password" minLength={12} value={nextPassword} disabled={!account} onChange={(e) => setNextPassword(e.target.value)} className="mt-1 w-full p-2.5 bg-slate-50 border border-slate-200 rounded-xl font-normal" />
                </label>
                <label className="text-xs font-bold text-slate-700">{t('prof.confirmPassword')}
                  <input type="password" minLength={12} value={confirmPassword} disabled={!account} onChange={(e) => setConfirmPassword(e.target.value)} className="mt-1 w-full p-2.5 bg-slate-50 border border-slate-200 rounded-xl font-normal" />
                </label>
              </div>
              {nextPassword !== '' && confirmPassword !== '' && nextPassword !== confirmPassword ? <p className="text-xs font-semibold text-rose-600">{t('prof.passwordMismatch')}</p> : null}
              <button type="button" disabled={!account || authPending || nextPassword.length < 12 || nextPassword !== confirmPassword} onClick={() => void changePassword()} className="px-5 py-2.5 bg-slate-900 text-white font-bold text-xs rounded-xl disabled:opacity-50">{t('prof.changePassword')}</button>
            </section>
          </div>
        )}

        {/* CANCELLATION MODAL */}
        {cancellingBookingId && (
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-950/70 backdrop-blur-xs">
            <div className="bg-white rounded-2xl p-6 max-w-md w-full shadow-2xl border border-slate-200 space-y-4">
              <div className="w-12 h-12 rounded-full bg-rose-100 text-rose-600 flex items-center justify-center mx-auto">
                <AlertCircle className="w-6 h-6" />
              </div>
              <div className="text-center">
                <h3 className="text-base font-extrabold text-slate-900">
                  {t('prof.cancelT')} #{cancellingBookingId}?
                </h3>
                <p className="text-xs text-slate-500 mt-2">
                  fleetava — <strong>{t('prof.freeCancelPol')}</strong>, {t('prof.cancelD')}
                </p>
              </div>

              <div className="flex gap-3 pt-2">
                <button
                  type="button"
                  onClick={() => setCancellingBookingId(null)}
                  className="grow py-2.5 bg-slate-100 hover:bg-slate-200 text-slate-700 text-xs font-bold rounded-xl"
                >{t('prof.keep')}</button>
                <button
                  type="button"
                  onClick={() => void confirmCancel()}
                  className="grow py-2.5 bg-rose-600 hover:bg-rose-700 text-white text-xs font-bold rounded-xl shadow-xs"
                >{t('prof.confirmCancel')}</button>
              </div>
            </div>
          </div>
        )}

        {/* MODIFY DATES MODAL */}
        {modifyingBooking && (
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-950/70 backdrop-blur-xs">
            <div className="bg-white rounded-2xl p-6 max-w-md w-full shadow-2xl border border-slate-200 space-y-4">
              <h3 className="text-base font-extrabold text-slate-900">
                {t('prof.modT')} #{modifyingBooking.id}
              </h3>
              <p className="text-xs text-slate-500">{t('prof.modD')}</p>

              <div className="space-y-3 text-xs">
                <div>
                  <label className="font-bold text-slate-700 block mb-1">{t('prof.newPickup')}</label>
                  <input
                    type="date"
                    value={modPickupDate}
                    onChange={(e) => setModPickupDate(e.target.value)}
                    className="w-full p-2.5 bg-slate-50 border border-slate-200 rounded-xl font-semibold"
                  />
                </div>
                <div>
                  <label className="font-bold text-slate-700 block mb-1">{t('prof.newReturn')}</label>
                  <input
                    type="date"
                    value={modDropoffDate}
                    onChange={(e) => setModDropoffDate(e.target.value)}
                    className="w-full p-2.5 bg-slate-50 border border-slate-200 rounded-xl font-semibold"
                  />
                </div>
              </div>

              <div className="flex gap-3 pt-2">
                <button
                  type="button"
                  onClick={() => setModifyingBooking(null)}
                  className="grow py-2.5 bg-slate-100 text-slate-700 text-xs font-bold rounded-xl"
                >
                  {t('prof.cancelBtn')}
                </button>
                <button
                  type="button"
                  onClick={() => void handleSaveModification()}
                  className="grow py-2.5 bg-[var(--color-primary)] text-white text-xs font-bold rounded-xl"
                >{t('prof.saveDates')}</button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function profileFields(profile: AccountProfile): ProfileForm {
  return {
    firstName: profile.firstName ?? '',
    lastName: profile.lastName ?? '',
    name: profile.name,
    phone: profile.phone ?? '',
    licenseNumber: profile.licenseNumber ?? '',
    locale: profile.locale || 'fr',
  };
}

function messageFrom(data: unknown, fallback: string): string {
  if (data !== null && typeof data === 'object') {
    const record = data as { error?: unknown; message?: unknown; errors?: Record<string, unknown> };
    if (typeof record.error === 'string') return record.error;
    const first = record.errors ? Object.values(record.errors).flat()[0] : null;
    if (typeof first === 'string') return first;
    if (typeof record.message === 'string') return record.message;
  }
  return fallback;
}
